import { useScopedModuleId } from "../contexts/ModuleScopeContext";
import { formatDateTime } from "../utils/date";
import { useFinanceScope } from "../contexts/FinanceScopeContext";
import React, { useEffect, useMemo, useState } from "react";
import AppShell from "../components/AppShell";
import { useAuth } from "../contexts/AuthContext";
import useModules from "../hooks/useModules";
import { api, API_BASE, formatApiError } from "../lib/api";
import { ROLES } from "../lib/rbac";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Download, Printer, FileText, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import ReportHeader from "../components/ReportHeader";
import DateInput from "../components/DateInput";

const ALL = "__all__";
const ATT_STATUSES = ["present", "late", "absent", "exception", "weekend", "holiday", "partial"];

const ROLE_READ_ONLY = ROLES.ASL_VIEWER || "asl_read_only_viewer";

const REPORTS = [
    { group: "Attendance", key: "attendance-daily", label: "Attendance Daily",
        filters: ["module", "date_range", "att_status"] },
    { group: "Attendance", key: "absenteeism", label: "Absenteeism",
        filters: ["module", "date_range"] },
    { group: "Attendance", key: "late-arrival", label: "Late Arrival",
        filters: ["module", "date_range", "min_late"] },
    { group: "Payroll", key: "payroll-summary", label: "Payroll Summary",
        filters: ["module", "date_range"] },
    { group: "Payroll", key: "casual-worker-payment", label: "Casual Worker Payment",
        filters: ["module", "date_range"] },
    { group: "Payroll", key: "management-fee", label: "Management Fee",
        filters: ["date_range"] },
    { group: "Workforce", key: "department-workforce", label: "Department Workforce",
        filters: ["module"] },
    { group: "Workforce", key: "contract-expiry", label: "Contract Expiry",
        filters: ["module", "expiry_days"] },
    { group: "Workforce", key: "employee-status", label: "Employee Status",
        filters: ["module"] },
];

// Finance-only whitelist used when Reports renders inside MasterFinanceShell.
const FINANCE_REPORT_KEYS = new Set([
    "payroll-summary", "casual-worker-payment", "management-fee", "contract-expiry",
]);

const monthStartIso = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); };
const todayIso = () => new Date().toISOString().slice(0, 10);

export default function Reports() {
    const { user } = useAuth();
    const { modules } = useModules();
    const isReadOnly = user?.role === ROLE_READ_ONLY;
    const isDeptMgr = user?.role === ROLES.ASL_DEPT_MGR;
    const scopedId = useScopedModuleId();
    const financeScope = useFinanceScope();
    const isLocked = !!scopedId || isDeptMgr;

    const visibleReports = useMemo(
        () => financeScope ? REPORTS.filter((r) => FINANCE_REPORT_KEYS.has(r.key)) : REPORTS,
        [financeScope],
    );

    const [active, setActive] = useState(visibleReports[0].key);
    // Keep `active` valid when finance scope changes
    useEffect(() => {
        if (!visibleReports.find((r) => r.key === active)) setActive(visibleReports[0].key);
    }, [visibleReports, active]);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);

    const [filters, setFilters] = useState({
        module_id: scopedId || ALL,
        date_from: monthStartIso(),
        date_to: todayIso(),
        status: ALL,
        min_late_minutes: 15,
        days: 30,
    });

    const report = useMemo(() => visibleReports.find((r) => r.key === active) || visibleReports[0], [active, visibleReports]);

    const queryString = useMemo(() => {
        const p = new URLSearchParams();
        if (report.filters.includes("module") && filters.module_id !== ALL) p.set("module_id", filters.module_id);
        if (report.filters.includes("date_range")) {
            if (filters.date_from) p.set("date_from", filters.date_from);
            if (filters.date_to) p.set("date_to", filters.date_to);
        }
        if (report.filters.includes("att_status") && filters.status !== ALL) p.set("status", filters.status);
        if (report.filters.includes("min_late")) p.set("min_late_minutes", filters.min_late_minutes);
        if (report.filters.includes("expiry_days")) p.set("days", filters.days);
        return p.toString();
    }, [active, filters, report]);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/reports/${active}?${queryString}`);
            setRows(data.items || []);
        } catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [active, queryString]);

    const headers = rows.length ? Object.keys(rows[0]) : [];
    const groups = ["Attendance", "Payroll", "Workforce"];

    // Build human-readable filter chips for the report header strip.
    const moduleName = useMemo(() => {
        if (filters.module_id === ALL) return "All modules";
        const m = modules.find((mm) => mm.id === filters.module_id);
        return m ? `${m.code} — ${m.name}` : filters.module_id;
    }, [filters.module_id, modules]);

    const headerFilters = useMemo(() => {
        const chips = [];
        if (report.filters.includes("module")) chips.push({ label: "Module", value: moduleName });
        if (report.filters.includes("date_range")) chips.push({ label: "Date", value: `${filters.date_from} → ${filters.date_to}` });
        if (report.filters.includes("att_status") && filters.status !== ALL) chips.push({ label: "Status", value: filters.status });
        if (report.filters.includes("min_late")) chips.push({ label: "Min late (min)", value: String(filters.min_late_minutes) });
        if (report.filters.includes("expiry_days")) chips.push({ label: "Within days", value: String(filters.days) });
        chips.push({ label: "Rows", value: String(rows.length) });
        return chips;
    }, [report, filters, moduleName, rows.length]);

    return (
        <AppShell title="Reports">
            <section className="flex gap-5" data-testid="reports-page">
                {/* Left nav */}
                <aside className="w-56 shrink-0">
                    <div className="asl-card p-3 space-y-3">
                        {groups.map((g) => (
                            <div key={g}>
                                <div className="text-[10px] uppercase tracking-wider text-[color:var(--asl-muted)] px-2 mb-1">{g}</div>
                                <div className="space-y-0.5">
                                    {visibleReports.filter((r) => r.group === g).map((r) => (
                                        <button
                                            key={r.key}
                                            onClick={() => setActive(r.key)}
                                            data-testid={`report-nav-${r.key}`}
                                            className={`block w-full text-left text-sm px-2 py-1.5 rounded ${
                                                active === r.key
                                                    ? "asl-navy-bg text-white font-semibold"
                                                    : "hover:bg-gray-50 asl-navy-text"
                                            }`}
                                        >
                                            {r.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </aside>

                {/* Right: filters + table */}
                <div className="flex-1 min-w-0 space-y-4 asl-print-area">
                    <ReportHeader title={report.label} filters={headerFilters} />

                    <div className="asl-card p-4 flex flex-wrap items-end gap-3 asl-print-hide" data-testid="report-filters">
                        {report.filters.includes("module") && !isDeptMgr && (
                            <Fld label="Module">
                                <Select value={filters.module_id} onValueChange={(v) => setFilters((f) => ({ ...f, module_id: v }))}>
                                    <SelectTrigger className="w-44" data-testid="rep-module"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value={ALL}>All modules</SelectItem>
                                        {modules.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </Fld>
                        )}
                        {report.filters.includes("date_range") && (
                            <>
                                <Fld label="From"><DateInput value={filters.date_from} className="w-40"
                                    onChange={(v) => setFilters((f) => ({ ...f, date_from: v }))} data-testid="rep-from" /></Fld>
                                <Fld label="To"><DateInput value={filters.date_to} className="w-40"
                                    onChange={(v) => setFilters((f) => ({ ...f, date_to: v }))} data-testid="rep-to" /></Fld>
                            </>
                        )}
                        {report.filters.includes("att_status") && (
                            <Fld label="Status">
                                <Select value={filters.status} onValueChange={(v) => setFilters((f) => ({ ...f, status: v }))}>
                                    <SelectTrigger className="w-40" data-testid="rep-status"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value={ALL}>All</SelectItem>
                                        {ATT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </Fld>
                        )}
                        {report.filters.includes("min_late") && (
                            <Fld label="Min late (min)">
                                <Input type="number" min="0" value={filters.min_late_minutes} className="w-32"
                                    onChange={(e) => setFilters((f) => ({ ...f, min_late_minutes: Number(e.target.value) }))} data-testid="rep-min-late" />
                            </Fld>
                        )}
                        {report.filters.includes("expiry_days") && (
                            <Fld label="Within days">
                                <Input type="number" min="1" value={filters.days} className="w-28"
                                    onChange={(e) => setFilters((f) => ({ ...f, days: Number(e.target.value) }))} data-testid="rep-days" />
                            </Fld>
                        )}
                        {!isReadOnly && (
                            <div className="ml-auto flex gap-2">
                                <a href={`${API_BASE}/reports/${active}?${queryString}&format=csv`} target="_blank" rel="noreferrer">
                                    <Button variant="outline" size="sm" data-testid={`rep-csv-${active}`}>
                                        <Download className="h-4 w-4 mr-1" /> Export CSV
                                    </Button>
                                </a>
                                <a href={`${API_BASE}/reports/${active}/export.pdf?${queryString}`} target="_blank" rel="noreferrer">
                                    <Button variant="outline" size="sm" data-testid={`rep-pdf-${active}`}>
                                        <FileText className="h-4 w-4 mr-1" /> Export PDF
                                    </Button>
                                </a>
                                <a href={`${API_BASE}/reports/${active}/export.xlsx?${queryString}`} target="_blank" rel="noreferrer">
                                    <Button variant="outline" size="sm" data-testid={`rep-xlsx-${active}`}>
                                        <FileSpreadsheet className="h-4 w-4 mr-1" /> Export Excel
                                    </Button>
                                </a>
                                <Button variant="outline" size="sm" onClick={() => window.print()} data-testid={`rep-print-${active}`}>
                                    <Printer className="h-4 w-4 mr-1" /> Print
                                </Button>
                            </div>
                        )}
                    </div>

                    <div className="asl-card overflow-hidden" data-testid="report-results">
                        {loading ? <div className="p-6 text-sm">Loading…</div>
                        : rows.length === 0 ? <div className="p-10 text-center text-sm text-[color:var(--asl-muted)]" data-testid="rep-empty">No rows.</div>
                        : <div className="overflow-x-auto">
                            <table className="asl-table asl-table--zebra" data-testid={`rep-table-${active}`}>
                                <thead className="text-[11px] uppercase tracking-wider text-[color:var(--asl-muted)]"
                                    style={{ background: "var(--asl-navy-50)" }}>
                                    <tr>
                                        {headers.map((h) => <th key={h} className="text-left px-3 py-2 whitespace-nowrap">{h.replace(/_/g, " ")}</th>)}
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.slice(0, 500).map((r, i) => (
                                        <tr key={i} className="border-t" style={{ borderColor: "var(--asl-border)" }}>
                                            {headers.map((h) => {
                                                const v = r[h];
                                                return <td key={h} className="px-3 py-2 text-xs whitespace-nowrap mono">
                                                    {typeof v === "object" && v !== null ? JSON.stringify(v) : String(v ?? "—")}
                                                </td>;
                                            })}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {rows.length > 500 && <div className="px-3 py-2 text-xs text-[color:var(--asl-muted)]">Showing first 500 of {rows.length} rows. Export CSV for full data.</div>}
                        </div>}
                    </div>
                    <div className="asl-print-footer" aria-hidden="true">
                        ASL HR Management Platform · Generated by {user?.email || "system"} on {formatDateTime()}
                    </div>
                </div>
            </section>
        </AppShell>
    );
}

function Fld({ label, children }) {
    return <div className="space-y-1">
        <div className="text-[10px] uppercase tracking-wider text-[color:var(--asl-muted)]">{label}</div>
        {children}
    </div>;
}
